import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Search,
  TrendingUp,
  Edit,
  Trash2,
  BarChart,
  AlertCircle,
  Loader2,
  ChevronLeft,
  ChevronRight,
  Eye,
  MousePointerClick,
  Package,
  CheckCircle
} from 'lucide-react';
import { vendorAdvertisementApi } from '../services/api';
import { Advertisement, AdvertisementAnalytics, AdType } from '../types';
import { extractApiError } from '../utils/errorUtils';
import EditAdvertisementModal from './modals/EditAdvertisementModal';
import AdvertisementAnalyticsModal from './modals/AdvertisementAnalyticsModal';

type SortBy = 'impressions' | 'clicks' | 'ctr' | 'created_at';

export default function VendorAdvertisements() {
  // State
  const [advertisements, setAdvertisements] = useState<Advertisement[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const pageSize = 12;

  // Search & Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState<AdType | 'all'>('all');
  const [filterStatus, setFilterStatus] = useState<'all' | 'active' | 'inactive'>('all');
  const [sortBy, setSortBy] = useState<SortBy>('created_at');

  // Modals
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [analyticsModalOpen, setAnalyticsModalOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [selectedAd, setSelectedAd] = useState<Advertisement | null>(null);
  const [selectedAdAnalytics, setSelectedAdAnalytics] = useState<AdvertisementAnalytics[] | null>(null);
  const [deletingAdId, setDeletingAdId] = useState<number | null>(null);

  // Load advertisements
  const loadAdvertisements = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await vendorAdvertisementApi.getAdvertisements({
        includeInactive: true, // We filter client-side for better UX
        page: currentPage,
        pageSize: pageSize
      });

      setAdvertisements(response.items);
      setTotalPages(response.total_pages);
      setTotalItems(response.total);
    } catch (err: unknown) {
      const apiError = extractApiError(err);
      console.error('Failed to load advertisements:', apiError);
      setError(apiError.message);
    } finally {
      setLoading(false);
    }
  }, [currentPage]);

  useEffect(() => {
    loadAdvertisements();
  }, [loadAdvertisements]);

  // Filter and sort advertisements
  const filteredAndSortedAds = useMemo(() => {
    let filtered = advertisements;

    // Apply search filter
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(ad =>
        ad.title.toLowerCase().includes(searchLower) ||
        (ad.description?.toLowerCase().includes(searchLower))
      );
    }

    // Apply type filter
    if (filterType !== 'all') {
      filtered = filtered.filter(ad => ad.ad_type === filterType);
    }

    // Apply status filter
    if (filterStatus === 'active') {
      filtered = filtered.filter(ad => ad.is_active);
    } else if (filterStatus === 'inactive') {
      filtered = filtered.filter(ad => !ad.is_active);
    }

    // Sort
    const sorted = [...filtered].sort((a, b) => {
      switch (sortBy) {
        case 'impressions':
          return b.impressions - a.impressions;
        case 'clicks':
          return b.clicks - a.clicks;
        case 'ctr': {
          const ctrA = a.impressions > 0 ? (a.clicks / a.impressions) * 100 : 0;
          const ctrB = b.impressions > 0 ? (b.clicks / b.impressions) * 100 : 0;
          return ctrB - ctrA;
        }
        case 'created_at':
        default:
          return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
      }
    });

    return sorted;
  }, [advertisements, searchTerm, filterType, filterStatus, sortBy]);

  // Handle edit
  const handleEdit = (ad: Advertisement) => {
    setSelectedAd(ad);
    setEditModalOpen(true);
  };

  // Handle save from edit modal
  const handleSaveEdit = async (updatedAd: Advertisement) => {
    setAdvertisements(prev =>
      prev.map(ad => ad.id === updatedAd.id ? updatedAd : ad)
    );
    setEditModalOpen(false);
    setSelectedAd(null);
    setSuccessMessage('Advertisement updated successfully!');
    setTimeout(() => setSuccessMessage(null), 3000);
  };

  // Handle delete
  const handleDeleteClick = (ad: Advertisement) => {
    setSelectedAd(ad);
    setDeleteConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!selectedAd) return;

    try {
      setDeletingAdId(selectedAd.id);
      setError(null);

      await vendorAdvertisementApi.deleteAdvertisement(selectedAd.id);

      // Remove from list or mark as inactive
      setAdvertisements(prev =>
        prev.map(ad => ad.id === selectedAd.id ? { ...ad, is_active: false } : ad)
      );

      setSuccessMessage('Advertisement deleted successfully!');
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err: unknown) {
      const apiError = extractApiError(err);
      console.error('Failed to delete advertisement:', apiError);
      setError(apiError.message);
      setTimeout(() => setError(null), 5000);
    } finally {
      setDeletingAdId(null);
      setDeleteConfirmOpen(false);
      setSelectedAd(null);
    }
  };

  // Handle view analytics
  const handleViewAnalytics = async (ad: Advertisement) => {
    try {
      setError(null);
      const analytics = await vendorAdvertisementApi.getAdvertisementAnalytics(ad.id);
      setSelectedAd(ad);
      setSelectedAdAnalytics(analytics);
      setAnalyticsModalOpen(true);
    } catch (err: unknown) {
      const apiError = extractApiError(err);
      console.error('Failed to load analytics:', apiError);
      setError(apiError.message);
      setTimeout(() => setError(null), 5000);
    }
  };

  // Calculate CTR
  const calculateCTR = (ad: Advertisement): number => {
    if (ad.impressions === 0) return 0;
    return (ad.clicks / ad.impressions) * 100;
  };

  // Format date
  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Render loading skeleton
  if (loading && advertisements.length === 0) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="animate-pulse" data-testid="loading-spinner">
          <div className="h-10 bg-gray-200 rounded w-1/3 mb-6"></div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3, 4, 5, 6].map(i => (
              <div key={i} className="bg-white rounded-lg border border-gray-200 p-6">
                <div className="h-6 bg-gray-200 rounded w-3/4 mb-4"></div>
                <div className="h-4 bg-gray-200 rounded w-full mb-2"></div>
                <div className="h-4 bg-gray-200 rounded w-2/3"></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="vendor-advertisements-container">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">My Advertisements</h1>
        <p className="mt-2 text-gray-600">
          Manage and track your advertisement campaigns
        </p>
      </div>

      {/* Success Message */}
      {successMessage && (
        <div className="mb-6 bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg flex items-center">
          <CheckCircle className="h-5 w-5 mr-2" />
          {successMessage}
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div
          className="mb-6 bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg flex items-center"
          data-testid="advertisements-error"
        >
          <AlertCircle className="h-5 w-5 mr-2 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {/* Search and Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Search */}
          <div className="relative">
            <label htmlFor="advertisement-search" className="sr-only">Search advertisements</label>
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              id="advertisement-search"
              type="text"
              placeholder="Search advertisements..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-dusty-teal focus:border-transparent"
              data-testid="advertisement-search-input"
              aria-label="Search advertisements"
            />
          </div>

          {/* Type Filter */}
          <div>
            <label htmlFor="advertisement-type-filter" className="sr-only">Ad Type</label>
            <select
              id="advertisement-type-filter"
              value={filterType}
              onChange={(e) => setFilterType(e.target.value as AdType | 'all')}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-dusty-teal focus:border-transparent"
              data-testid="advertisement-type-filter"
              aria-label="Ad Type"
            >
              <option value="all">All Types</option>
              <option value="youtube">YouTube</option>
              <option value="etsy">Etsy</option>
              <option value="direct">Direct</option>
              <option value="custom">Custom</option>
            </select>
          </div>

          {/* Status Filter */}
          <div>
            <label htmlFor="advertisement-status-filter" className="sr-only">Status</label>
            <select
              id="advertisement-status-filter"
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value as 'all' | 'active' | 'inactive')}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-dusty-teal focus:border-transparent"
              data-testid="advertisement-status-filter"
              aria-label="Status"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>

          {/* Sort */}
          <div>
            <label htmlFor="advertisement-sort-select" className="sr-only">Sort By</label>
            <select
              id="advertisement-sort-select"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as SortBy)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-dusty-teal focus:border-transparent"
              data-testid="advertisement-sort-select"
              aria-label="Sort By"
            >
              <option value="created_at">Newest First</option>
              <option value="impressions">Most Impressions</option>
              <option value="clicks">Most Clicks</option>
              <option value="ctr">Highest CTR</option>
            </select>
          </div>
        </div>
      </div>

      {/* Results Summary */}
      <div className="mb-4 text-sm text-gray-600">
        Showing {filteredAndSortedAds.length} of {totalItems} advertisements
      </div>

      {/* Advertisement Grid */}
      {filteredAndSortedAds.length === 0 ? (
        <div
          className="bg-white rounded-lg border border-gray-200 p-12 text-center"
          data-testid="empty-state"
        >
          <Package className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No advertisements found</h3>
          <p className="text-gray-600 mb-6">
            {searchTerm || filterType !== 'all' || filterStatus !== 'all'
              ? 'Try adjusting your filters to find more results.'
              : 'You haven\'t purchased any advertisements yet.'}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredAndSortedAds.map((ad) => (
            <article
              key={ad.id}
              className="bg-white rounded-lg border border-gray-200 hover:shadow-lg transition-shadow duration-200"
              data-testid={`advertisement-card-${ad.id}`}
            >
              {/* Card Header with Image */}
              {ad.image_url && (
                <div className="relative h-48 rounded-t-lg overflow-hidden bg-gray-100">
                  <img
                    src={ad.image_url}
                    alt={ad.title}
                    className="w-full h-full object-cover"
                  />
                  {!ad.is_active && (
                    <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center">
                      <span className="bg-red-600 text-white px-3 py-1 rounded-full text-sm font-semibold">
                        Inactive
                      </span>
                    </div>
                  )}
                </div>
              )}

              <div className="p-6">
                {/* Title and Type */}
                <div className="flex items-start justify-between mb-3">
                  <h3 className="text-lg font-semibold text-gray-900 flex-1 mr-2">
                    {ad.title}
                  </h3>
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-moonbone text-ink">
                    {ad.ad_type}
                  </span>
                </div>

                {/* Description */}
                {ad.description && (
                  <p className="text-sm text-gray-600 mb-4 line-clamp-2">
                    {ad.description}
                  </p>
                )}

                {/* Stats */}
                <div className="grid grid-cols-3 gap-3 mb-4 pb-4 border-b border-gray-200">
                  <div className="text-center">
                    <div className="flex items-center justify-center text-gray-500 mb-1">
                      <Eye className="h-4 w-4 mr-1" />
                    </div>
                    <div className="text-lg font-semibold text-gray-900">
                      {ad.impressions.toLocaleString()}
                    </div>
                    <div className="text-xs text-gray-500">Impressions</div>
                  </div>

                  <div className="text-center">
                    <div className="flex items-center justify-center text-gray-500 mb-1">
                      <MousePointerClick className="h-4 w-4 mr-1" />
                    </div>
                    <div className="text-lg font-semibold text-gray-900">
                      {ad.clicks.toLocaleString()}
                    </div>
                    <div className="text-xs text-gray-500">Clicks</div>
                  </div>

                  <div className="text-center">
                    <div className="flex items-center justify-center text-gray-500 mb-1">
                      <TrendingUp className="h-4 w-4 mr-1" />
                    </div>
                    <div className="text-lg font-semibold text-gray-900">
                      {calculateCTR(ad).toFixed(2)}%
                    </div>
                    <div className="text-xs text-gray-500">CTR</div>
                  </div>
                </div>

                {/* Dates */}
                {(ad.start_date || ad.end_date) && (
                  <div className="text-xs text-gray-500 mb-4">
                    <div className="flex justify-between">
                      <span>{ad.start_date ? `Start: ${formatDate(ad.start_date)}` : 'No start date'}</span>
                      <span>{ad.end_date ? `End: ${formatDate(ad.end_date)}` : 'No end date'}</span>
                    </div>
                  </div>
                )}

                {/* Actions */}
                <div className="flex gap-2">
                  <button
                    onClick={() => handleEdit(ad)}
                    className="flex-1 inline-flex items-center justify-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-dusty-teal"
                    data-testid={`advertisement-edit-btn-${ad.id}`}
                    aria-label={`Edit advertisement: ${ad.title}`}
                  >
                    <Edit className="h-4 w-4 mr-1" />
                    Edit
                  </button>

                  <button
                    onClick={() => handleViewAnalytics(ad)}
                    className="flex-1 inline-flex items-center justify-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-dusty-teal"
                    data-testid={`advertisement-analytics-btn-${ad.id}`}
                    aria-label={`View analytics for ${ad.title}`}
                  >
                    <BarChart className="h-4 w-4 mr-1" />
                    Analytics
                  </button>

                  <button
                    onClick={() => handleDeleteClick(ad)}
                    disabled={deletingAdId === ad.id}
                    className="px-3 py-2 border border-red-300 text-sm font-medium rounded-lg text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50"
                    data-testid={`advertisement-delete-btn-${ad.id}`}
                    aria-label={`Delete advertisement: ${ad.title}`}
                  >
                    {deletingAdId === ad.id ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Trash2 className="h-4 w-4" />
                    )}
                  </button>
                </div>
              </div>
            </article>
          ))}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="mt-8 flex items-center justify-between">
          <div className="text-sm text-gray-700">
            Page {currentPage} of {totalPages}
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
              className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              aria-label="Previous page"
            >
              <ChevronLeft className="h-4 w-4 mr-1" />
              Previous
            </button>
            <button
              onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
              disabled={currentPage === totalPages}
              className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              aria-label="Next page"
            >
              Next
              <ChevronRight className="h-4 w-4 ml-1" />
            </button>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirmOpen && selectedAd && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full p-6" role="dialog" aria-labelledby="delete-modal-title" aria-modal="true">
            <h3 id="delete-modal-title" className="text-lg font-semibold text-gray-900 mb-2">Delete Advertisement</h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete "{selectedAd.title}"? This will deactivate the advertisement.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => {
                  setDeleteConfirmOpen(false);
                  setSelectedAd(null);
                }}
                className="px-4 py-2 border border-gray-300 text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmDelete}
                disabled={deletingAdId !== null}
                className="px-4 py-2 border border-transparent text-sm font-medium rounded-lg text-white bg-red-600 hover:bg-red-700 disabled:opacity-50"
              >
                {deletingAdId ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin inline mr-2" />
                    Deleting...
                  </>
                ) : (
                  'Delete'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {editModalOpen && selectedAd && (
        <EditAdvertisementModal
          advertisement={selectedAd}
          onClose={() => {
            setEditModalOpen(false);
            setSelectedAd(null);
          }}
          onSave={handleSaveEdit}
        />
      )}

      {/* Analytics Modal */}
      {analyticsModalOpen && selectedAd && selectedAdAnalytics && (
        <AdvertisementAnalyticsModal
          advertisement={selectedAd}
          analytics={selectedAdAnalytics}
          onClose={() => {
            setAnalyticsModalOpen(false);
            setSelectedAd(null);
            setSelectedAdAnalytics(null);
          }}
        />
      )}
    </div>
  );
}
